<template>
    <modal @close="onClose" ref="modal">
        <template v-slot:title>Turn on two-step verification</template>

        <slot>
            <div class="step-label text-small text-muted mb-1" data-test="turn-on-step">Step {{ step }} of 3</div>
            <form-errors :errors="formErrors"/>

            <!-- Step 1: methods -->
            <template v-if="step === 1">
                <section v-if="hasPhones" class="mb-3" data-test="turn-on-phones">
                    <h4>Your Trusted Phones</h4>
                    <p>Review your trusted devices below. Each one can approve your sign-ins, so remove any you don't recognize.</p>
                    <div v-for="device in status.mobile_devices" :key="device.id" class="d-flex align-items-start border rounded p-1 mb-1" :class="{'bg-light': isRemoved(device)}" :data-test="`phone-${device.id}`">
                        <span class="mr-auto">
                            <span class="d-block text-semibold" :class="{'text-muted removed': isRemoved(device)}">{{ device.label }}</span>
                            <span class="d-block text-small text-muted" v-if="isRemoved(device)">Will stop being trusted when you turn this on</span>
                            <span class="d-block text-small text-muted" v-else>
                                <template v-if="device.user_label">{{ device.device_model_formatted }} · </template>Registered {{ $filters.fromNow(device.created_on) }}<template v-if="device.last_used">, last used {{ $filters.fromNow(device.last_used) }}</template>
                            </span>
                        </span>
                        <a href v-if="isRemoved(device)" class="text-small ml-2" :data-test="`undo-phone-${device.id}`" @click.prevent="undoRemove(device)">Undo</a>
                        <a href v-else class="text-small ml-2" :data-test="`remove-phone-${device.id}`" @click.prevent="removePhone(device)">Remove</a>
                    </div>
                </section>

                <section class="mb-3" data-test="turn-on-factors">
                    <h4>{{ hasPhones ? 'Other Verification Methods' : 'Verification Methods' }}</h4>
                    <p class="text-small text-muted">{{ methodsLine }}</p>
                    <ul class="list-unstyled mb-1" v-if="addedMethods.length" data-test="turn-on-added">
                        <li v-for="m in addedMethods" :key="m.key" class="d-flex align-items-baseline bg-light rounded px-1 py-hf mb-hf">
                            <i class="far fa-check text-success mr-1" aria-hidden="true"></i>
                            <span class="text-semibold mr-1">{{ m.name }}</span>
                            <span class="text-small text-muted mr-auto">{{ m.kind }}</span>
                            <a href class="text-small" :data-test="`remove-${m.key}`" @click.prevent="removeMethod(m)">Remove</a>
                        </li>
                    </ul>
                    <div class="method border rounded p-1 mb-1">
                        <div class="method-text">
                            <div class="text-semibold">Authenticator app</div>
                            <div class="text-small text-muted">Enter a 6-digit code from an app like Google Authenticator or Authy.</div>
                        </div>
                        <button type="button" class="btn btn-outline-primary btn-sm" data-test="add-totp" :disabled="busy" @click.prevent="addTotp">Add an authenticator app</button>
                    </div>
                    <div class="method border rounded p-1 mb-1" v-if="passkeySupported">
                        <div class="method-text">
                            <div class="text-semibold">Passkey</div>
                            <div class="text-small text-muted">Sign in with your fingerprint, face or screen lock on this device.</div>
                        </div>
                        <button type="button" class="btn btn-outline-primary btn-sm" data-test="add-passkey" :disabled="busy" @click.prevent="onAddPasskey">Add a passkey</button>
                    </div>
                </section>

                <p class="text-small text-muted">Next, you'll get backup codes to keep somewhere safe.</p>

                <button-row>
                    <button type="button" class="btn btn-outline-primary" data-test="turn-on-cancel" @click.prevent="close">Cancel</button>
                    <button type="button" class="btn btn-primary" data-test="to-codes" :disabled="busy || !canContinue" @click.prevent="toCodes">Continue</button>
                </button-row>
                <p v-if="!canContinue" class="text-small text-muted text-right mt-1">Keep a phone or add a method to continue.</p>
            </template>

            <!-- Step 2: backup codes, made before anything is turned on -->
            <template v-else-if="step === 2">
                <section class="mb-3" data-test="turn-on-codes">
                    <h4>Save Your Backup Codes</h4>
                    <p>Each code works once if you can't use your other methods. Keep them somewhere safe – they won't be shown again.</p>
                    <backup-codes-list :codes="codes" />
                    <label class="saved-check d-flex align-items-center border rounded p-1 mt-3 mb-0 text-semibold">
                        <input type="checkbox" class="mr-1" v-model="saved" data-test="codes-saved">
                        I've saved these codes somewhere safe
                    </label>
                </section>

                <button-row>
                    <button type="button" class="btn btn-outline-primary" data-test="back-to-methods" @click.prevent="step = 1">Back</button>
                    <button type="button" class="btn btn-primary" data-test="to-confirm" :disabled="!saved" @click.prevent="step = 3">Continue</button>
                </button-row>
                <p v-if="!saved" class="text-small text-muted text-right mt-1">Confirm you've saved the codes to continue.</p>
            </template>

            <!-- Step 3: confirm; Turn on is the only step that changes anything -->
            <template v-else>
                <section class="mb-3" data-test="turn-on-confirm">
                    <p>After your password, you'll confirm it's you with one of these:</p>
                    <ul class="list-unstyled mb-2" data-test="turn-on-summary">
                        <li v-for="d in keptPhones" :key="`d${d.id}`" class="bg-light rounded px-1 py-hf mb-hf">
                            <span class="text-semibold mr-1">{{ d.label }}</span><span class="text-small text-muted">Trusted phone</span>
                        </li>
                        <li v-for="m in addedMethods" :key="m.key" class="bg-light rounded px-1 py-hf mb-hf">
                            <span class="text-semibold mr-1">{{ m.name }}</span><span class="text-small text-muted">{{ m.kind }}</span>
                        </li>
                    </ul>
                    <alert-box type="warning" v-if="removedPhones.length" data-test="turn-on-dropping">
                        <template v-for="(d, i) in removedPhones" :key="d.id"><template v-if="i">, </template><b>{{ d.label }}</b></template>
                        will stop being trusted.
                    </alert-box>
                    <p class="text-small text-muted">If you can't use any of these, use a backup code. Emailed sign-in codes will stop working for your account.</p>
                </section>

                <button-row>
                    <button type="button" class="btn btn-outline-primary" data-test="back-to-codes" :disabled="busy" @click.prevent="step = 2">Back</button>
                    <button type="button" class="btn btn-primary" data-test="enroll" :disabled="busy" @click.prevent="onEnroll">Turn on</button>
                </button-row>
            </template>
        </slot>
    </modal>
</template>

<script>
import { browserSupportsWebAuthn } from '@simplewebauthn/browser'
import api from '@/api'
import ModalMixin from '@/mixins/ModalMixin'
import BackupCodesList from './BackupCodesList'
import { report } from './mfaHelpers'

const BASE = '/account/mfa'

export default {
    components: {BackupCodesList},
    mixins: [ModalMixin],
    props: {
        status: {type: Object, required: true},
        guard: {type: Function, required: true},
    },
    emits: ['changed', 'enrolled', 'add-totp', 'add-passkey', 'close'],
    data() {
        return {
            step: 1,
            removedIds: [],
            // made when step 2 first opens, and kept on Back and forward
            codes: [],
            batch: '',
            saved: false,
            busy: false,
            formErrors: [],
            passkeySupported: browserSupportsWebAuthn(),
        }
    },
    computed: {
        hasPhones() {
            return this.status.mobile_devices.length > 0
        },
        keptPhones() {
            return this.status.mobile_devices.filter((d) => !this.isRemoved(d))
        },
        removedPhones() {
            return this.status.mobile_devices.filter((d) => this.isRemoved(d))
        },
        addedMethods() {
            return [
                ...this.status.totp_devices.map((d) => ({key: `totp-${d.id}`, id: d.id, type: 'totp', name: d.name, kind: 'Authenticator app'})),
                ...this.status.passkeys.map((p) => ({key: `passkey-${p.id}`, id: p.id, type: 'passkeys', name: p.name, kind: 'Passkey'})),
            ]
        },
        // Mirrors the server, which needs a kept phone or another method
        canContinue() {
            return this.keptPhones.length > 0 || this.addedMethods.length > 0
        },
        methodsLine() {
            if (this.keptPhones.length) {
                return 'Recommended, so you can still sign in without your phone.'
            }
            return this.hasPhones ? 'Add at least one, since no phone will be trusted.' : 'Add at least one.'
        },
    },
    methods: {
        // A fresh run. open() alone resumes one that stepped aside for a sub-modal.
        start() {
            this.step = 1
            this.removedIds = []
            this.forgetCodes()
            this.busy = false
            this.formErrors = []
            this.open()
        },
        forgetCodes() {
            this.codes = []
            this.batch = ''
            this.saved = false
        },
        onClose() {
            // a sub-modal only opens from step 1, where there are no codes yet
            this.forgetCodes()
            this.$emit('close')
        },
        isRemoved(device) {
            return this.removedIds.includes(device.id)
        },
        removePhone(device) {
            this.removedIds.push(device.id)
        },
        undoRemove(device) {
            this.removedIds = this.removedIds.filter((id) => id !== device.id)
        },
        addTotp() {
            this.$emit('add-totp')
            this.close()
        },
        onAddPasskey() {
            this.$emit('add-passkey')
            this.close()
        },
        removeMethod(m) {
            if (!confirm(`Remove "${m.name}"?`)) {
                return
            }
            this.run(() => this.guard(() => api.post(`${BASE}/${m.type}/${m.id}/remove`, {}))).then(() => this.$emit('changed'))
        },
        toCodes() {
            if (this.codes.length) {
                this.step = 2
                return
            }
            this.run(() => this.guard(() => api.post(`${BASE}/backup-codes/prepare`, {}))).then((resp) => {
                this.codes = resp.backup_codes
                this.batch = resp.batch
                this.saved = false
                this.step = 2
            })
        },
        onEnroll() {
            const body = {keep_device_ids: this.keptPhones.map((d) => d.id), backup_codes_batch: this.batch}
            this.run(() => this.guard(() => api.post(`${BASE}/enroll`, body)), (err) => {
                if (err && err.reason === 'backup_codes_changed') {
                    this.forgetCodes()
                    this.step = 1
                }
            }).then(() => {
                this.forgetCodes()
                this.$emit('enrolled')
                this.close()
            })
        },
        // Runs fn with busy set. Resolves only on success; failures are shown here.
        run(fn, onError) {
            this.busy = true
            this.formErrors = []
            return new Promise((resolve) => {
                fn().then((resp) => {
                    this.busy = false
                    resolve(resp)
                }).catch((errors) => {
                    this.busy = false
                    if (onError) {
                        onError(errors)
                    }
                    this.formErrors = (errors && !errors.__shown__ && errors.__all__) || []
                    report(errors)
                })
            })
        },
    },
}
</script>

<style scoped>
.step-label {
    text-transform: uppercase;
    letter-spacing: 0.06em;
}
.removed {
    text-decoration: line-through;
}
.method {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
}
.method-text {
    flex: 1 1 220px;
    min-width: 0;
}
.saved-check {
    cursor: pointer;
}
</style>
